import React, { useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  fontSize,
  visuallyHidden,
} from '../styles';
import { Container, Heading, Svg } from './ui';
import Filters from './Filters';
import PackageCard from './PackageCard';
import SpaVouchers from './SpaVouchers';
import SpaEnquiry from './SpaEnquiry';
import PackagePurchase from './PackagePurchase';
import heartsLeft from '../images/site-themes/valentines/hearts-left.svg';
import heartsRight from '../images/site-themes/valentines/hearts-right.svg';
import flowersLeft from '../images/site-themes/mothers-day/flowers-left.png';
import flowersRight from '../images/site-themes/mothers-day/flowers-right.png';
import winterBush from '../images/site-themes/christmas/winter-bush.png';

const StyledSpaPackages = styled.section`
  ${sectionMargins()};

  ${minBreakpointQuery.xlarge`
    position: relative;
    overflow: hidden;
  `}
`;

const StyledHearts = styled(Svg)`
  ${maxBreakpointQuery.xlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.xlarge`
    position: absolute;
    top: 150px;
  `}

  &:first-of-type {
    ${minBreakpointQuery.large`
      left: -100px;
    `}

    ${minBreakpointQuery.xlarge`
      left: -50px;
    `}

    ${minBreakpointQuery.xxlarge`
      left: 0;
    `}

    ${minBreakpointQuery.xxxlarge`
      left: 50px;
    `}
  }

  &:last-of-type {
    ${minBreakpointQuery.large`
      right: -100px;
    `}

    ${minBreakpointQuery.xlarge`
      right: -50px;
    `}

    ${minBreakpointQuery.xxlarge`
      right: 0;
    `}

    ${minBreakpointQuery.xxxlarge`
      right: 50px;
    `}
  }
`;

const StyledFlowers = styled.img`
  ${maxBreakpointQuery.xlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.xlarge`
    position: absolute;
  `}

  &:first-of-type {
    ${minBreakpointQuery.xlarge`
      top: 180px;
      left: 0;
    `}
  }

  &:last-of-type {
    ${minBreakpointQuery.xlarge`
      top: 220px;
      right: 0;
    `}
  }
`;

const StyledContainer = styled(Container)`
  ${minBreakpointQuery.xlarge`
    position: relative;
  `}
`;

const StyledHeader = styled.header`
  margin-bottom: 30px;

  ${minBreakpointQuery.small`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 50px;
  `}
`;

const StyledHeadingPhone = styled.div`
  ${minBreakpointQuery.small`
    display: flex;
    justify-content: space-between;
    align-items: false;
  `}
`;

const StyledPhone = styled.p`
  ${maxBreakpointQuery.small`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.small`
    text-align: right;
  `}
`;

const StyledPhoneText = styled.span`
  ${minBreakpointQuery.small`
    display: block;
    margin-bottom: 2px;
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)};
  `}
`;

const StyledPhoneLink = styled.a`
  ${maxBreakpointQuery.small`
    display: block;
    padding: 5px;
  `}
`;

const StyledPhoneNumber = styled.span`
  ${minBreakpointQuery.small`
    ${fontSize(21)};
    white-space: nowrap;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(24)};
  `}
`;

const StyledItems = styled.div`
  display: grid;
  gap: 10px;

  ${minBreakpointQuery.small`
    gap: 20px;
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.mlarge`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${minBreakpointQuery.large`
    row-gap: 30px;
  `}
`;

const StyledWinterBush = styled.img`
  ${maxBreakpointQuery.xlarge`
    ${visuallyHidden()};
  `}

  &:first-of-type {
    ${minBreakpointQuery.xlarge`
      position: absolute;
      top: 240px;
      left: -160px;
      max-height: 260px;
    `}
  }

  &:last-of-type {
    ${minBreakpointQuery.xlarge`
      position: absolute;
      top: 360px;
      right: -280px;
      max-height: 360px;
    `}
  }
`;

const StyledSpaVoucherEnquiry = styled.section`
  margin: 10px 0;

  ${minBreakpointQuery.small`
    margin-top: 20px;
    margin-bottom: 20px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
    margin-bottom: 30px;
  `}
`;

const StyledWrapper = styled.div`
  ${minBreakpointQuery.smedium`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  `}
`;

const SpaPackages = ({
  items,
  spa,
  toggleSpaEnquiryOpen,
  altTheme,
  giftVouchers,
  minAge,
  defaultActiveFilters,
  activeFilters,
  setActiveFilters,
  isSpaEnquiryOpen,
}) => {
  const {
    spaseekers: {
      site: { telephone },
      promotion: { theme: siteTheme },
    },
  } = useStaticQuery(graphql`
    query SpaPackagesQuery {
      spaseekers {
        site {
          telephone
        }
        promotion {
          theme
        }
      }
    }
  `);

  const filters = [
    {
      name: 'All',
      value: 'all',
    },
  ];

  const findFlag = flagToFind => items.find(({ flags }) => flags[flagToFind]);

  if (findFlag('isDeal')) {
    filters.push({
      name: 'Spa Deals',
      value: 'spa-deals',
    });
  }

  if (findFlag('isDay')) {
    filters.push({
      name: 'Spa Days',
      value: 'spa-days',
    });
  }

  if (findFlag('isBreak')) {
    filters.push({
      name: 'Spa Breaks',
      value: 'spa-breaks',
    });
  }

  if (findFlag('isVoucher')) {
    filters.push({
      name: 'Spa Vouchers',
      value: 'spa-vouchers',
    });
  }

  const [displayedItems, setDisplayedItems] = useState(items);

  const [selectedPackageForBooking, setSelectedPackageForBooking] = useState({
    id: '',
    name: '',
    minPrice: '',
    isPricePerPerson: '',
    liveAvailability: '',
    checkAvailability: '',
    availableForBooking: '',
    flags: {
      isBooking: '',
      isBreak: '',
      isVoucher: '',
    },
    yielding: false,
    initialPurchaseType: undefined,
  });
  const [showPackagePurchaseOverlay, setShowPackagePurchaseOverlay] =
    useState(false);

  const handleActiveFilters = (value, activeFilters) => {
    if (value === 'all') {
      setActiveFilters(defaultActiveFilters);
    } else {
      setActiveFilters({
        ...activeFilters,
        all: false,
        [`${value}`]: !activeFilters[value],
      });
    }
  };

  const changeSelectedPackageForBooking = data => {
    setSelectedPackageForBooking(data);
    setShowPackagePurchaseOverlay(true);
  };

  useEffect(() => {
    const filteredItems = activeFilters.all
      ? items
      : items.filter(
          ({ flags }) =>
            (activeFilters['spa-deals'] && flags.isDeal) ||
            (activeFilters['spa-days'] && flags.isDay) ||
            (activeFilters['spa-breaks'] && flags.isBreak) ||
            (activeFilters['spa-vouchers'] && flags.isVoucher),
        );

    setDisplayedItems(filteredItems);
  }, [items, activeFilters]);

  return (
    items.length > 0 && (
      <StyledSpaPackages id="spa-packages">
        {siteTheme === 'valentines' ? (
          <>
            <StyledHearts image={heartsLeft} />
            <StyledHearts image={heartsRight} />
          </>
        ) : siteTheme === 'mothersday' ? (
          <>
            <StyledFlowers src={flowersLeft} alt="Flowers" loading="lazy" />
            <StyledFlowers src={flowersRight} alt="Flowers" loading="lazy" />
          </>
        ) : (
          ''
        )}
        <StyledContainer>
          <StyledHeader>
            <StyledHeadingPhone>
              <Heading>Packages</Heading>
              <StyledPhone>
                <StyledPhoneText>Bookings &amp; Advice</StyledPhoneText>
                <StyledPhoneLink href={`tel:${telephone}`}>
                  <StyledPhoneNumber>{telephone}</StyledPhoneNumber>
                </StyledPhoneLink>
              </StyledPhone>
            </StyledHeadingPhone>
            <Filters
              heading="Filter Packages"
              items={filters}
              activeFilters={activeFilters}
              handleActiveFilters={handleActiveFilters}
              altTheme={altTheme}
            />
          </StyledHeader>
          {displayedItems.length > 0 && (
            <StyledItems>
              {displayedItems.map(
                ({
                  packageId,
                  name,
                  features,
                  url,
                  deal,
                  liveAvailability,
                  checkAvailability,
                  availableForBooking,
                  earliestDate,
                  minPrice,
                  higherPrice,
                  isPricePerPerson,
                  guestsPerPackage,
                  maxGuestsPerPackage,
                  maxOccupancy,
                  voucherOptions,
                  minQuantity,
                  flags,
                  yielding,
                }) => (
                  <PackageCard
                    key={packageId}
                    id={packageId}
                    name={name}
                    points={features}
                    url={url}
                    deal={deal}
                    liveAvailability={liveAvailability}
                    checkAvailability={checkAvailability}
                    availableForBooking={availableForBooking}
                    earliestDate={earliestDate}
                    minPrice={minPrice}
                    higherPrice={higherPrice}
                    isPricePerPerson={isPricePerPerson}
                    guestsPerPackage={guestsPerPackage}
                    maxGuestsPerPackage={maxGuestsPerPackage}
                    maxOccupancy={maxOccupancy}
                    voucherOptions={voucherOptions}
                    minQuantity={minQuantity}
                    flags={flags}
                    highlighted={deal || altTheme}
                    yielding={yielding}
                    changeSelectedPackageForBooking={
                      changeSelectedPackageForBooking
                    }
                  />
                ),
              )}
            </StyledItems>
          )}
          {siteTheme === 'christmas' && (
            <>
              <StyledWinterBush
                src={winterBush}
                alt="Winter Bush"
                loading="lazy"
              />
              <StyledWinterBush
                src={winterBush}
                alt="Winter Bush"
                loading="lazy"
              />
            </>
          )}
        </StyledContainer>
        <StyledSpaVoucherEnquiry>
          <Container>
            <StyledWrapper>
              {giftVouchers && <SpaVouchers />}
              <SpaEnquiry
                spaId={spa.id}
                spaName={spa.name}
                packages={displayedItems}
                minAge={minAge}
                toggleSpaEnquiryOpen={toggleSpaEnquiryOpen}
                isSpaEnquiryOpen={isSpaEnquiryOpen}
                altTheme={altTheme}
              />
            </StyledWrapper>
          </Container>
        </StyledSpaVoucherEnquiry>
        <PackagePurchase
          id={selectedPackageForBooking.id}
          name={selectedPackageForBooking.name}
          minPrice={selectedPackageForBooking.minPrice}
          isPricePerPerson={selectedPackageForBooking.isPricePerPerson}
          liveAvailability={selectedPackageForBooking.liveAvailability}
          checkAvailability={selectedPackageForBooking.checkAvailability}
          availableForBooking={selectedPackageForBooking.availableForBooking}
          earliestDate={selectedPackageForBooking.earliestDate}
          maxOccupancy={selectedPackageForBooking.maxOccupancy}
          voucherOptions={selectedPackageForBooking.voucherOptions}
          guestsPerPackage={selectedPackageForBooking.guestsPerPackage}
          maxGuestsPerPackage={selectedPackageForBooking.maxGuestsPerPackage}
          minQuantity={selectedPackageForBooking.minQuantity}
          flags={selectedPackageForBooking.flags}
          yielding={selectedPackageForBooking.yielding}
          initialPurchaseType={selectedPackageForBooking.initialPurchaseType}
          spa={spa}
          isOverlay={true}
          showOverlay={showPackagePurchaseOverlay}
          setShowOverlay={setShowPackagePurchaseOverlay}
        />
      </StyledSpaPackages>
    )
  );
};

export default SpaPackages;
